import {
  CANONICAL_COLUMNS,
  type CarrierConfigDraft,
  type MappingRowDraft,
  mappingRowDraft,
  skipRuleDraft,
} from '@commissionsight/shared';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { IconButton, btnGhost, input } from './ui.js';

type Patch = Dispatch<SetStateAction<CarrierConfigDraft>>;

const cell = `${input} text-sm`;
const lbl = 'mb-1 block text-[11px] font-medium uppercase tracking-wide text-neutral-500';

// PII columns a privacy policy may target (mirrors packages/config guardrails;
// member_external_id is hash-only — it's NOT-NULL when stored).
const PRIV_DROPPABLE = [
  'member_first_name',
  'member_last_name',
  'member_full_name',
  'member_dob',
  'email',
  'phone',
  'address_line1',
  'address_line2',
  'city',
  'state',
  'postal_code',
  'policy_number',
  'writing_agent',
];
const PRIV_HASHABLE = ['member_external_id', ...PRIV_DROPPABLE];

/** Fully structured editor for a carrier mapping config — replaces raw-JSON editing.
 *  Drives entirely off CarrierConfigDraft (round-trip is pure + unit-tested). */
export function CarrierConfigEditor({
  draft,
  setDraft,
  sourceHeaders = [],
}: {
  draft: CarrierConfigDraft;
  setDraft: Patch;
  sourceHeaders?: string[];
}) {
  const [advanced, setAdvanced] = useState(false);
  const listId = 'cfg-src-headers';

  const patchRow = (id: string, fn: (r: MappingRowDraft) => MappingRowDraft) =>
    setDraft((d) => ({ ...d, mappings: d.mappings.map((r) => (r.id === id ? fn(r) : r)) }));

  const mappedTargets = new Set(draft.mappings.map((m) => m.target).filter(Boolean));
  const privCols = PRIV_HASHABLE.filter((c) => mappedTargets.has(c));

  return (
    <div className="space-y-4">
      <datalist id={listId}>
        {sourceHeaders.map((h) => (
          <option key={h} value={h} />
        ))}
      </datalist>

      {/* Format + identity */}
      <div className="grid gap-3 sm:grid-cols-3">
        <label>
          <span className={lbl}>Carrier slug</span>
          <input
            className={cell}
            value={draft.carrierSlug}
            onChange={(e) => setDraft((d) => ({ ...d, carrierSlug: e.target.value }))}
          />
        </label>
        <label>
          <span className={lbl}>Product line</span>
          <select
            className={cell}
            value={draft.productLine}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                productLine: e.target.value as CarrierConfigDraft['productLine'],
              }))
            }
          >
            <option value="major_medical">Major medical</option>
            <option value="medicare">Medicare</option>
            <option value="ancillary">Ancillary</option>
          </select>
        </label>
        <label>
          <span className={lbl}>File type</span>
          <select
            className={cell}
            value={draft.fileType}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                fileType: e.target.value as CarrierConfigDraft['fileType'],
              }))
            }
          >
            <option value="csv">CSV</option>
            <option value="xlsx">XLSX</option>
          </select>
        </label>
        <label>
          <span className={lbl}>Header row</span>
          <input
            className={cell}
            type="number"
            min="1"
            value={draft.headerRow}
            onChange={(e) => setDraft((d) => ({ ...d, headerRow: e.target.value }))}
          />
        </label>
        {draft.fileType === 'xlsx' && (
          <label>
            <span className={lbl}>Sheet name</span>
            <input
              className={cell}
              placeholder="(first sheet)"
              value={draft.sheetName}
              onChange={(e) => setDraft((d) => ({ ...d, sheetName: e.target.value }))}
            />
          </label>
        )}
        {draft.fileType === 'csv' && (
          <label>
            <span className={lbl}>Delimiter</span>
            <input
              className={cell}
              value={draft.delimiter}
              onChange={(e) => setDraft((d) => ({ ...d, delimiter: e.target.value }))}
            />
          </label>
        )}
        <label>
          <span className={lbl}>Member-id column</span>
          <input
            className={cell}
            list={listId}
            placeholder="source header"
            value={draft.memberKey}
            onChange={(e) => setDraft((d) => ({ ...d, memberKey: e.target.value }))}
          />
        </label>
        <label>
          <span className={lbl}>Policy-number column (optional)</span>
          <input
            className={cell}
            list={listId}
            value={draft.policyKey}
            onChange={(e) => setDraft((d) => ({ ...d, policyKey: e.target.value }))}
          />
        </label>
        <label>
          <span className={lbl}>ID normalization</span>
          <select
            className={cell}
            value={draft.idNormalization}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                idNormalization: e.target.value as CarrierConfigDraft['idNormalization'],
              }))
            }
          >
            <option value="default">Default (keep as-is)</option>
            <option value="numeric">Numeric (strip leading zeros)</option>
          </select>
        </label>
      </div>

      {/* Column mapping */}
      <div>
        <div className={lbl}>Column mapping</div>
        <div className="space-y-1.5">
          {draft.mappings.map((row) => (
            <div key={row.id} className="flex flex-wrap items-center gap-1.5">
              <input
                className={`${cell} w-44`}
                list={listId}
                placeholder="source header"
                value={row.source}
                onChange={(e) => patchRow(row.id, (r) => ({ ...r, source: e.target.value }))}
              />
              <span className="text-neutral-400">→</span>
              <select
                className={`${cell} w-48`}
                value={row.target}
                onChange={(e) => patchRow(row.id, (r) => ({ ...r, target: e.target.value }))}
              >
                <option value="">(ignore — keep in raw)</option>
                {CANONICAL_COLUMNS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
                {row.target.startsWith('extra.') && (
                  <option value={row.target}>{row.target}</option>
                )}
              </select>
              <TransformControls row={row} patchRow={patchRow} />
              <IconButton
                icon="remove"
                label="Remove mapping"
                tone="rose"
                onClick={() =>
                  setDraft((d) => ({ ...d, mappings: d.mappings.filter((r) => r.id !== row.id) }))
                }
              />
            </div>
          ))}
        </div>
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            className={btnGhost}
            onClick={() =>
              setDraft((d) => ({ ...d, mappings: [...d.mappings, mappingRowDraft()] }))
            }
          >
            + Map a column
          </button>
          <button
            type="button"
            className={btnGhost}
            onClick={() => {
              const name = prompt('Custom field name (stored under extra.<name>):');
              if (name?.trim())
                setDraft((d) => ({
                  ...d,
                  mappings: [...d.mappings, mappingRowDraft({ target: `extra.${name.trim()}` })],
                }));
            }}
          >
            + Map to extra.*
          </button>
        </div>
      </div>

      {/* Privacy */}
      <div className="rounded-lg border border-neutral-200 p-3">
        <div className={lbl}>Privacy &amp; data minimization</div>
        {privCols.length === 0 ? (
          <p className="text-xs text-neutral-400">No PII columns mapped — nothing to minimize.</p>
        ) : (
          <div className="space-y-1.5">
            {privCols.map((col) => {
              const mode = draft.privacyDrop.includes(col)
                ? 'drop'
                : draft.privacyHash.includes(col)
                  ? 'hash'
                  : 'store';
              return (
                <div key={col} className="flex items-center justify-between gap-2 text-sm">
                  <span className="font-mono text-xs text-neutral-600">{col}</span>
                  <select
                    className={`${cell} w-28`}
                    value={mode}
                    onChange={(e) =>
                      setDraft((d) => {
                        const drop = d.privacyDrop.filter((c) => c !== col);
                        const hash = d.privacyHash.filter((c) => c !== col);
                        if (e.target.value === 'drop') drop.push(col);
                        else if (e.target.value === 'hash') hash.push(col);
                        return { ...d, privacyDrop: drop, privacyHash: hash };
                      })
                    }
                  >
                    <option value="store">Store</option>
                    <option value="hash">Hash</option>
                    {col !== 'member_external_id' && <option value="drop">Drop</option>}
                  </select>
                </div>
              );
            })}
          </div>
        )}
        <label className="mt-3 flex items-center gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={!draft.keepRawRow}
            onChange={(e) => setDraft((d) => ({ ...d, keepRawRow: !e.target.checked }))}
          />
          Don’t retain the original source row (strongest minimization)
        </label>
      </div>

      {/* Advanced */}
      <div className="rounded-lg border border-neutral-200">
        <button
          type="button"
          className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-neutral-500"
          onClick={() => setAdvanced((v) => !v)}
        >
          Advanced (messy-file rules)
          <span>{advanced ? '–' : '+'}</span>
        </button>
        {advanced && (
          <div className="space-y-3 border-t border-neutral-100 p-3">
            <label className="block">
              <span className={lbl}>
                Positional columns (one per line — overrides the header row)
              </span>
              <textarea
                className={`${cell} h-20`}
                value={draft.columns}
                onChange={(e) => setDraft((d) => ({ ...d, columns: e.target.value }))}
              />
            </label>
            <label className="block">
              <span className={lbl}>
                Require columns (skip a row when any is empty — one per line)
              </span>
              <textarea
                className={`${cell} h-16`}
                value={draft.requireColumns}
                onChange={(e) => setDraft((d) => ({ ...d, requireColumns: e.target.value }))}
              />
            </label>
            <div>
              <span className={lbl}>Skip rows where (column matches regex)</span>
              <div className="space-y-1.5">
                {draft.skipRows.map((s) => (
                  <div key={s.id} className="flex items-center gap-1.5">
                    <input
                      className={`${cell} w-44`}
                      placeholder="column"
                      value={s.column}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          skipRows: d.skipRows.map((x) =>
                            x.id === s.id ? { ...x, column: e.target.value } : x,
                          ),
                        }))
                      }
                    />
                    <input
                      className={`${cell} w-44`}
                      placeholder="regex (e.g. ^total)"
                      value={s.pattern}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          skipRows: d.skipRows.map((x) =>
                            x.id === s.id ? { ...x, pattern: e.target.value } : x,
                          ),
                        }))
                      }
                    />
                    <IconButton
                      icon="remove"
                      label="Remove rule"
                      tone="rose"
                      onClick={() =>
                        setDraft((d) => ({
                          ...d,
                          skipRows: d.skipRows.filter((x) => x.id !== s.id),
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
              <button
                type="button"
                className={`${btnGhost} mt-1.5`}
                onClick={() =>
                  setDraft((d) => ({ ...d, skipRows: [...d.skipRows, skipRuleDraft()] }))
                }
              >
                + Skip rule
              </button>
            </div>
            <label className="block">
              <span className={lbl}>Required fields for a valid row (one per line)</span>
              <textarea
                className={`${cell} h-16`}
                placeholder="member_external_id&#10;commission_amount"
                value={draft.validationRequired}
                onChange={(e) => setDraft((d) => ({ ...d, validationRequired: e.target.value }))}
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
}

/** Inline transform editor for one mapping row. */
function TransformControls({
  row,
  patchRow,
}: {
  row: MappingRowDraft;
  patchRow: (id: string, fn: (r: MappingRowDraft) => MappingRowDraft) => void;
}) {
  const t = row.transform;
  const setT = (patch: Partial<MappingRowDraft['transform']>) =>
    patchRow(row.id, (r) => ({ ...r, transform: { ...r.transform, ...patch } }));
  return (
    <>
      <select
        className={`${cell} w-28`}
        value={t.type}
        onChange={(e) => setT({ type: e.target.value as MappingRowDraft['transform']['type'] })}
        title="Value transform (Auto = sensible default for the field)"
      >
        <option value="">Auto</option>
        <option value="currency">currency</option>
        <option value="number">number</option>
        <option value="integer">integer</option>
        <option value="percent">percent</option>
        <option value="date">date</option>
        <option value="excel-date">excel-date</option>
        <option value="string">string</option>
      </select>
      {(t.type === 'currency' || t.type === 'number' || t.type === 'integer') && (
        <input
          className={`${cell} w-24`}
          placeholder="strip e.g. $,"
          value={t.stripChars}
          onChange={(e) => setT({ stripChars: e.target.value })}
        />
      )}
      {t.type === 'percent' && (
        <label className="flex items-center gap-1 text-xs text-neutral-600">
          <input
            type="checkbox"
            checked={t.asFraction}
            onChange={(e) => setT({ asFraction: e.target.checked })}
          />
          ÷100
        </label>
      )}
      {t.type === 'date' && (
        <input
          className={`${cell} w-28`}
          placeholder="MM/DD/YYYY"
          value={t.format}
          onChange={(e) => setT({ format: e.target.value })}
        />
      )}
      {t.type === 'string' && (
        <span className="flex items-center gap-1.5 text-xs text-neutral-600">
          <label className="flex items-center gap-0.5">
            <input
              type="checkbox"
              checked={t.trim}
              onChange={(e) => setT({ trim: e.target.checked })}
            />
            trim
          </label>
          <label className="flex items-center gap-0.5">
            <input
              type="checkbox"
              checked={t.uppercase}
              onChange={(e) => setT({ uppercase: e.target.checked })}
            />
            UC
          </label>
        </span>
      )}
    </>
  );
}
